import { useState, useRef, useCallback, useEffect } from "react";
import { useQueryClient, useQuery, useMutation } from "@tanstack/react-query";
import { usePageTitle } from "@/hooks/use-page-title";
import { useRoute, useLocation, Link } from "wouter";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import type { Property, Room } from "@shared/schema";
import { isPrimaryRoom } from "@shared/fees";
import AppLayout from "@/components/app-layout";
import { ViewOnlyBanner } from "@/hooks/use-permissions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  CheckCircle2,
  Circle,
  ChevronRight,
  ChevronLeft,
  Rocket,
  PackageOpen,
  ClipboardList,
  Building2,
  AlertCircle,
  Lock,
  Plus,
  Minus,
  Sparkles,
  Loader2,
  BedDouble,
  Camera,
  Upload,
  Settings2,
  Video,
} from "lucide-react";
import { PADSPLIT_PROPERTY_TYPES } from "@shared/schema";
import { differenceInDays } from "date-fns";
import { ExplainerVideoButton } from "@/components/explainer-video-button";
import { GuideButton } from "@/components/guide-button";
import { GuideNudgeBanner } from "@/components/guide-nudge-banner";

const DEFAULT_PARKING_NOTES = "Park in the driveway or on the street in front of the house, or in the backyard if you absolutely have to. If you absolutely have to park in front of a neighbor's house, do not park in front of a mailbox, a driveway, or within 10 ft of the driveway, as that's where their trash cans will need to go.";

const HOME_DESCRIPTION_WORDS = ["Newly Renovated", "Cozy", "Modern", "Beautiful", "Comfortable", "High Quality", "Spacious", "Homey"];
const LOCATION_DESCRIPTION_WORDS = ["Close to Public Transit", "On a Cul-de-sac", "Up and Coming", "Near Highways", "Quiet", "Lively", "Near Grocery Stores"];

const AMENITIES_OPTIONS = {
  exterior: ["Garage", "Backyard", "Patio or Porch", "Pool"],
  communal: ["Dining Area", "Living Room", "Workspace", "Television"],
  kitchen: ["More Than One Fridge", "Upgraded Appliances", "Dishwasher", "Basic Cookware", "Garbage Disposal"],
  bedrooms: ["Bed Linens"],
  safety: ["Fire Extinguisher", "Fire Stops", "Carbon Monoxide Detectors", "First Aid Kit", "Outdoor Security Cameras", "Security Alarm", "Outdoor Lights"],
};

const DAYS_OF_WEEK = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

const BEDROOM_REQUIREMENTS = [
  "A bed frame, mattress, and cover",
  "Clothing storage (closet or dresser)",
  "A numerical label on the door",
  "Two operable modes of egress to the exterior",
  "A working smoke detector",
  "A door lock",
  "At least 80 sq ft with shortest wall at least 7 ft long",
  "HVAC supply",
  "Minimum of two working outlets",
  "A working light source (floor lamp or ceiling light)",
];

const REQUIREMENTS_CONFIG = [
  { key: "twoWayLocks", label: "All accessible exterior doors have two-way locks", section: "General" },
  { key: "flooringGoodRepair", label: "All flooring is in good repair and sealed with no trip hazard", section: "General" },
  { key: "stairsHaveRailings", label: "All stairs must have railings", section: "General" },
  { key: "windowsOperable", label: "All windows are operable (can open, close, and lock)", section: "General" },
  { key: "carbonMonoxideDetectors", label: "Carbon monoxide detectors", section: "General" },
  { key: "majorAppliances", label: "All major functioning appliances (fridge, sink, microwave, oven, and stove)", section: "Kitchen" },
  { key: "smokeDetector", label: "A smoke detector", section: "Kitchen" },
  { key: "fireExtinguisherKitchen", label: "A fire extinguisher and/or fire stops near the stove", section: "Kitchen" },
  { key: "showerWithCurtain", label: "Each full bathroom has a shower with shower curtain or door installed", section: "Bathrooms" },
  { key: "trashCan", label: "A trash can to prevent flushing of unwanted items", section: "Bathrooms" },
  { key: "lockingDoor", label: "A door that locks", section: "Bathrooms" },
  { key: "maxFourPerBathroom", label: "Maximum of four people to one bathroom", section: "Bathrooms" },
];

const DEFAULT_REQUIREMENTS: Record<string, boolean> = {
  twoWayLocks: false, flooringGoodRepair: false, stairsHaveRailings: false,
  windowsOperable: false, carbonMonoxideDetectors: false, majorAppliances: false,
  smokeDetector: false, fireExtinguisherKitchen: false, showerWithCurtain: false,
  trashCan: false, lockingDoor: false, maxFourPerBathroom: false,
};

interface BedroomDetail {
  roomIndex: number;
  floorLevel: string;
  stairsRequired: boolean;
  roomSize: string;
  // Optional physical dimensions (ft). Stored as sizeCategory + squareFootage
  // on the room record for Phase 2 analytics grouping. Set by the host during
  // launch; PadSplit size-category (roomSize) acts as the analytics proxy when
  // these are absent.
  widthFt?: string;
  lengthFt?: string;
  bedSize: string;
  occupants: string;
  bathroomType: string;
  closetType: string;
  windowCount: number;
  doorLockType: string;
  smartLockRemote: boolean;
  smartLockCode: string;
  smartLockAddLater: boolean;
  climateControl: string;
  bedroomLabelPhoto: string;
  useComingSoonLabel: boolean;
  uniqueFeatures: string[];
  roomDescription: string;
  weeklyPrice: string;
  moveInFee: string;
}

interface PhotoSection {
  exterior: string[];
  commonSpaces: string[];
  bedrooms: Record<number, string[]>;
  bathrooms: Record<number, { photos: string[]; assignedBedrooms: number[] }>;
  other: string[];
  videoTourUrl: string;
  threeDTourUrl: string;
  bestPhotosFirst: boolean;
}

interface WizardData {
  addressConfirmed?: boolean;
  unitId?: string;
  listingType?: string;
  bedroomCount?: number;
  bathroomCount?: number;
  hasSteps?: boolean;
  homeSizeSqFt?: number;
  isOwner?: boolean | null;
  requirements?: Record<string, boolean>;
  amenities?: Record<string, string[]>;
  hasWasherDryer?: boolean;
  washerDryerLocation?: string;
  washerDryerPayRequired?: boolean;
  parkingAvailable?: boolean;
  parkingSpots?: { street: number; driveway: number; garage: number };
  parkingNotes?: string;
  hasTrashPickupDays?: boolean | null;
  trashPickupDays?: string[];
  hasRecyclingPickupDays?: boolean;
  recyclingPickupDays?: string[];
  trashPickupFrequency?: string;
  trashNotes?: string;
  restockingSupplies?: boolean | null;
  professionalCleaning?: boolean | null;
  homeDescriptionWords?: string[];
  locationDescriptionWords?: string[];
  nearbyAttractions?: string;
  listingDescription?: string;
  listingTitle?: string;
  part1Complete?: boolean;
  bedroomRequirementsConfirmed?: boolean;
  bedroomDetails?: BedroomDetail[];
  defaultMoveInFee?: string;
  part2Complete?: boolean;
  photos?: PhotoSection;
  part3Complete?: boolean;
  movingInstructions?: string;
  lockCodes?: string;
  part4Complete?: boolean;
  currentPart?: number;
  currentStep?: number;
}

function computeActivationTimeframe(launchDate: string | null | undefined): string {
  if (!launchDate) return "not_sure";
  const today = new Date();
  const launch = new Date(launchDate);
  const days = differenceInDays(launch, today);
  if (days <= 0) return "1_3_weeks";
  if (days <= 21) return "1_3_weeks";
  if (days <= 60) return "1_2_months";
  if (days <= 120) return "3_4_months";
  if (days <= 180) return "5_6_months";
  if (days > 180) return "more_than_6_months";
  return "not_sure";
}

function timeframeLabel(tf: string): string {
  const map: Record<string, string> = {
    "1_3_weeks": "1–3 weeks", "1_2_months": "1–2 months", "3_4_months": "3–4 months",
    "5_6_months": "5–6 months", "more_than_6_months": "More than 6 months", "not_sure": "Not sure yet",
  };
  return map[tf] || "Not sure yet";
}

type ChecklistItem = { step: number; label: string; description: string; complete: boolean };

function buildChecklist(property: Property, rooms: Room[]): ChecklistItem[] {
  const hasAddress = !!(property.address && property.address.trim());
  const hasPadsplitType = !!property.padsplitPropertyType;
  const hasOwnerOccupancy = property.ownerOccupancy !== null && property.ownerOccupancy !== undefined;
  const roomImages = rooms.filter(r => (r.roomType || "bedroom") === "bedroom" && isPrimaryRoom(r) && r.outputImageUrl);
  const hasRoomPhoto = roomImages.length > 0;
  const hasLaunchDate = !!(property.launchDate && property.launchDate.trim());
  const hasRoomPricing = !!(property.minWeeklyPrice && property.maxWeeklyPrice);

  return [
    { step: 1, label: "Property address", description: "Set the property address", complete: hasAddress },
    { step: 2, label: "Property type", description: "Select the property type (House, Apartments, ADU, etc.)", complete: hasPadsplitType },
    { step: 3, label: "Owner occupancy", description: "Specify whether the owner will occupy the property", complete: hasOwnerOccupancy },
    { step: 4, label: "Room photos", description: "At least one generated room photo is required", complete: hasRoomPhoto },
    { step: 5, label: "Launch date & activation timeframe", description: "Set the launch date so PadSplit knows when to expect activation", complete: hasLaunchDate },
    { step: 6, label: "Room pricing (weekly rates)", description: "Configure min/max weekly rates (defaults: $180–$260)", complete: hasRoomPricing },
  ];
}

function YesNoToggle({ value, onChange, testId }: { value: boolean | null | undefined; onChange: (v: boolean) => void; testId: string }) {
  return (
    <div className="flex gap-2">
      <button type="button" className={`px-4 py-1.5 rounded-md text-sm font-medium border transition-colors ${value === true ? "bg-green-600 text-white border-green-600" : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"}`} onClick={() => onChange(true)} data-testid={`${testId}-yes`}>Yes</button>
      <button type="button" className={`px-4 py-1.5 rounded-md text-sm font-medium border transition-colors ${value === false ? "bg-gray-700 text-white border-gray-700" : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"}`} onClick={() => onChange(false)} data-testid={`${testId}-no`}>No</button>
    </div>
  );
}

function NumberStepper({ value, onChange, min, max, step: stepVal, testId }: { value: number; onChange: (v: number) => void; min: number; max: number; step: number; testId: string }) {
  return (
    <div className="flex items-center gap-3">
      <button type="button" className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-50 disabled:opacity-30" onClick={() => onChange(Math.max(min, +(value - stepVal).toFixed(1)))} disabled={value <= min} data-testid={`${testId}-minus`}><Minus className="h-3 w-3" /></button>
      <span className="text-lg font-semibold min-w-[3rem] text-center" data-testid={`${testId}-value`}>{value}</span>
      <button type="button" className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-50 disabled:opacity-30" onClick={() => onChange(Math.min(max, +(value + stepVal).toFixed(1)))} disabled={value >= max} data-testid={`${testId}-plus`}><Plus className="h-3 w-3" /></button>
    </div>
  );
}

function MultiSelectChips({ options, selected, onChange, max, testId }: { options: string[]; selected: string[]; onChange: (v: string[]) => void; max?: number; testId: string }) {
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((opt) => {
        const isSelected = selected.includes(opt);
        const atMax = max ? selected.length >= max && !isSelected : false;
        return (
          <button key={opt} type="button" className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${isSelected ? "bg-blue-600 text-white border-blue-600" : atMax ? "bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed" : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"}`} onClick={() => isSelected ? onChange(selected.filter(s => s !== opt)) : !atMax && onChange([...selected, opt])} disabled={atMax} data-testid={`${testId}-${opt.toLowerCase().replace(/\s+/g, "-")}`}>{opt}</button>
        );
      })}
    </div>
  );
}

function OptionButton({ label, selected, onClick, testId }: { label: string; selected: boolean; onClick: () => void; testId: string }) {
  return (
    <button type="button" className={`px-3 py-1.5 rounded-md text-sm border transition-colors ${selected ? "bg-blue-600 text-white border-blue-600" : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"}`} onClick={onClick} data-testid={testId}>{label}</button>
  );
}

function DaySelector({ selected, onChange, testId }: { selected: string[]; onChange: (v: string[]) => void; testId: string }) {
  return (
    <div className="flex flex-wrap gap-2">
      {DAYS_OF_WEEK.map((day) => {
        const isSelected = selected.includes(day);
        return <button key={day} type="button" className={`px-3 py-1.5 rounded-md text-sm border transition-colors ${isSelected ? "bg-blue-600 text-white border-blue-600" : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"}`} onClick={() => isSelected ? onChange(selected.filter(d => d !== day)) : onChange([...selected, day])} data-testid={`${testId}-${day.toLowerCase()}`}>{day.substring(0, 3)}</button>;
      })}
    </div>
  );
}

function PhotoUploader({ photos, onUpload, onRemove, testId }: { photos: string[]; onUpload: (url: string) => void; onRemove: (idx: number) => void; testId: string }) {
  const [uploading, setUploading] = useState(false);
  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const reader = new FileReader();
      reader.onload = async () => {
        const base64 = (reader.result as string).split(",")[1];
        const res = await apiRequest("POST", "/api/app/upload", { filename: file.name, mimeType: file.type, data: base64 });
        const data = await res.json();
        onUpload(data.imageUrl);
        setUploading(false);
      };
      reader.readAsDataURL(file);
    } catch { setUploading(false); }
  };
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-2">
        {photos.map((url, i) => (
          <div key={i} className="relative w-20 h-20 rounded border overflow-hidden group">
            <img src={url} alt="" className="w-full h-full object-cover" />
            <button type="button" className="absolute top-0 right-0 bg-gray-700 text-white text-xs w-5 h-5 rounded-bl opacity-0 group-hover:opacity-100 transition-opacity" onClick={() => onRemove(i)} data-testid={`${testId}-remove-${i}`}>&times;</button>
          </div>
        ))}
      </div>
      <label className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-dashed border-gray-300 text-sm text-gray-600 hover:bg-gray-50 cursor-pointer" data-testid={`${testId}-browse`}>
        {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
        {uploading ? "Uploading..." : "Browse"}
        <input type="file" accept="image/*" className="hidden" onChange={handleFile} disabled={uploading} />
      </label>
    </div>
  );
}

function PartHeader({ currentPart, part1Complete, part2Complete, part3Complete, part4Complete, onPartClick }: { currentPart: number; part1Complete: boolean; part2Complete: boolean; part3Complete: boolean; part4Complete: boolean; onPartClick: (p: number) => void }) {
  const parts = [
    { num: 1, label: "Property Details", enabled: true, complete: part1Complete },
    { num: 2, label: "Bedroom Details", enabled: part1Complete, complete: part2Complete },
    { num: 3, label: "Upload Photos", enabled: part2Complete, complete: part3Complete },
    { num: 4, label: "Finishing Setup", enabled: part3Complete, complete: part4Complete },
  ];
  return (
    <div className="flex items-center gap-2 mb-6 overflow-x-auto pb-2">
      {parts.map((part, i) => (
        <div key={part.num} className="flex items-center gap-2 shrink-0">
          {i > 0 && <div className={`w-8 h-px ${part.enabled ? "bg-blue-300" : "bg-gray-200"}`} />}
          <button type="button" disabled={!part.enabled} onClick={() => part.enabled && onPartClick(part.num)} className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
            currentPart === part.num ? "bg-blue-600 text-white" :
            part.complete ? "bg-green-50 text-green-700 border border-green-200 cursor-pointer hover:bg-green-100" :
            part.enabled ? "bg-gray-50 text-gray-700 border border-gray-200 cursor-pointer hover:bg-gray-100" :
            "bg-gray-50 text-gray-400 border border-gray-100 cursor-not-allowed"
          }`} data-testid={`part-header-${part.num}`}>
            {part.complete ? <CheckCircle2 className="h-4 w-4" /> : part.enabled ? <Circle className="h-4 w-4" /> : <Lock className="h-3.5 w-3.5" />}
            <span>Part {part.num}: {part.label}</span>
          </button>
        </div>
      ))}
    </div>
  );
}

function FurnishingProgress({ rooms, isLoading }: { rooms: Room[]; isLoading: boolean }) {
  if (isLoading) return <Skeleton className="h-16 w-full" />;
  const categoryOf = (r: Room): "room" | "bathroom" | "common" | "exterior" => {
    const t = r.roomType || "bedroom";
    if (t === "bathroom") return "bathroom";
    if (t === "common" || t === "common_area") return "common";
    if (t === "exterior") return "exterior";
    return "room";
  };
  return (
    <div className="border rounded-lg p-4">
      <div className="flex items-center gap-2 mb-3">
        <PackageOpen className="h-4 w-4 text-blue-600" />
        <h3 className="font-semibold text-gray-900 text-sm">Furnishing Progress</h3>
      </div>
      {rooms.length === 0 ? (
        <div className="flex items-center gap-2 text-xs text-gray-500 bg-gray-50 rounded p-2" data-testid="text-no-furnishing-data">
          <AlertCircle className="h-3.5 w-3.5 text-gray-400 shrink-0" />
          <span>No rooms found.</span>
        </div>
      ) : (
        <div className="space-y-2">
          {(["room", "bathroom", "common", "exterior"] as const).map((spaceType) => {
            const typeRows = rooms.filter(r => categoryOf(r) === spaceType);
            if (typeRows.length === 0) return null;
            const completedRows = typeRows.filter(r => r.outputImageUrl);
            const label = spaceType === "room" ? "Rooms" : spaceType === "bathroom" ? "Bathrooms" : spaceType === "common" ? "Common Areas" : "Exterior";
            const pct = Math.round((completedRows.length / typeRows.length) * 100);
            return (
              <div key={spaceType} data-testid={`furnishing-row-${spaceType}`}>
                <div className="flex items-center justify-between text-xs mb-1">
                  <span className="text-gray-700 font-medium">{label}</span>
                  <span className="text-gray-500">{completedRows.length}/{typeRows.length}</span>
                </div>
                <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                  <div className={`h-full rounded-full transition-all ${pct === 100 ? "bg-green-500" : pct > 0 ? "bg-blue-400" : "bg-gray-200"}`} style={{ width: `${pct}%` }} data-testid={`furnishing-progress-${spaceType}`} />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function LaunchChecklist({ property, rooms, onLaunch, launchPending }: { property: Property; rooms: Room[]; onLaunch: () => void; launchPending: boolean }) {
  const checklist = buildChecklist(property, rooms);
  const completedCount = checklist.filter(c => c.complete).length;
  const allComplete = completedCount === 6;
  const activationTimeframe = computeActivationTimeframe(property.launchDate);
  const alreadyLaunched = !!property.launchedAt;

  return (
    <div className="border rounded-lg p-4">
      <div className="flex items-center gap-2 mb-1">
        <ClipboardList className="h-4 w-4 text-indigo-600" />
        <h3 className="font-semibold text-gray-900 text-sm">Launch Readiness</h3>
      </div>
      <p className="text-xs text-gray-500 mb-3">{completedCount} of 6 items complete</p>
      <div className="mb-3 h-1.5 bg-gray-100 rounded-full overflow-hidden">
        <div className={`h-full rounded-full transition-all ${allComplete ? "bg-green-500" : "bg-blue-400"}`} style={{ width: `${Math.round((completedCount / 6) * 100)}%` }} data-testid="checklist-progress-bar" data-tour="property-launch-progress" />
      </div>
      <div className="space-y-1.5 mb-3" data-tour="property-launch-steps">
        {checklist.map((item) => (
          <div key={item.step} className={`flex items-center gap-2 p-2 rounded text-xs ${item.complete ? "bg-green-50" : "bg-red-50"}`} data-testid={`checklist-item-${item.step}`}>
            {item.complete ? <CheckCircle2 className="h-3.5 w-3.5 text-green-500 shrink-0" /> : <AlertCircle className="h-3.5 w-3.5 text-red-400 shrink-0" />}
            <span className={item.complete ? "text-green-800" : "text-red-700"}>{item.label}</span>
          </div>
        ))}
      </div>
      {property.launchDate && (
        <p className="text-xs text-indigo-600 mb-3">
          Activation: <strong>{timeframeLabel(activationTimeframe)}</strong>
        </p>
      )}
      {alreadyLaunched ? (
        <div className="flex items-center gap-2 bg-green-50 border border-green-200 rounded p-2" data-testid="banner-already-launched">
          <CheckCircle2 className="h-4 w-4 text-green-500 shrink-0" />
          <p className="text-xs font-semibold text-green-800">Launched on PadSplit</p>
        </div>
      ) : (
        <Button size="sm" className="w-full bg-brand hover:bg-brand-hover text-xs" disabled={!allComplete || launchPending} onClick={onLaunch} data-testid="button-launch-padsplit">
          <Rocket className="h-3.5 w-3.5 mr-1" />
          {launchPending ? "Launching..." : allComplete ? "Launch Property on PadSplit" : `${completedCount}/6 complete`}
        </Button>
      )}
    </div>
  );
}

function Part1Wizard({ property, wizardData, onSave }: { property: Property; wizardData: WizardData; onSave: (data: Partial<WizardData>) => void }) {
  const { toast } = useToast();
  const [step, setStep] = useState(wizardData.currentPart === 1 ? (wizardData.currentStep || 1) : 1);
  const d = wizardData;

  const [localBedrooms, setLocalBedrooms] = useState<number>(d.bedroomCount ?? 2);
  const [localBathrooms, setLocalBathrooms] = useState<number>(d.bathroomCount ?? 2.5);
  const initialSqft = d.homeSizeSqFt ?? 0;
  const [localSqftStr, setLocalSqftStr] = useState<string>(String(initialSqft));
  const bedroomTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const bathroomTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const sqftTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleBedroomChange = useCallback((v: number) => {
    setLocalBedrooms(v);
    if (bedroomTimerRef.current) clearTimeout(bedroomTimerRef.current);
    bedroomTimerRef.current = setTimeout(() => onSave({ bedroomCount: v }), 600);
  }, [onSave]);

  const handleBathroomChange = useCallback((v: number) => {
    setLocalBathrooms(v);
    if (bathroomTimerRef.current) clearTimeout(bathroomTimerRef.current);
    bathroomTimerRef.current = setTimeout(() => onSave({ bathroomCount: v }), 600);
  }, [onSave]);

  const handleSqftChange = useCallback((raw: string) => {
    setLocalSqftStr(raw);
    if (sqftTimerRef.current) clearTimeout(sqftTimerRef.current);
    sqftTimerRef.current = setTimeout(() => onSave({ homeSizeSqFt: parseInt(raw) || 0 }), 600);
  }, [onSave]);

  useEffect(() => {
    return () => {
      if (bedroomTimerRef.current) clearTimeout(bedroomTimerRef.current);
      if (bathroomTimerRef.current) clearTimeout(bathroomTimerRef.current);
      if (sqftTimerRef.current) clearTimeout(sqftTimerRef.current);
    };
  }, []);

  const cancelPendingTimers = () => {
    if (bedroomTimerRef.current) { clearTimeout(bedroomTimerRef.current); bedroomTimerRef.current = null; }
    if (bathroomTimerRef.current) { clearTimeout(bathroomTimerRef.current); bathroomTimerRef.current = null; }
    if (sqftTimerRef.current) { clearTimeout(sqftTimerRef.current); sqftTimerRef.current = null; }
  };

  const localStepperValues = (): Partial<WizardData> => ({
    bedroomCount: localBedrooms,
    bathroomCount: localBathrooms,
    homeSizeSqFt: parseInt(localSqftStr) || 0,
  });

  const goNext = (updates: Partial<WizardData>) => {
    cancelPendingTimers();
    const ns = step + 1;
    onSave({ ...localStepperValues(), ...updates, currentStep: ns, currentPart: 1 });
    setStep(ns);
  };
  const goBack = () => {
    cancelPendingTimers();
    const ps = Math.max(1, step - 1);
    onSave({ ...localStepperValues(), currentStep: ps, currentPart: 1 });
    setStep(ps);
  };

  const generateListingMutation = useMutation({
    meta: { silentSave: true },
    mutationFn: async () => { const res = await apiRequest("POST", `/api/app/properties/${property.id}/generate-listing`, {}); return res.json(); },
    onSuccess: (data: { description: string }) => onSave({ listingDescription: data.description }),
    onError: () => toast({ title: "Failed to generate description", variant: "destructive" }),
  });
  const generateTitleMutation = useMutation({
    meta: { silentSave: true },
    mutationFn: async () => { const res = await apiRequest("POST", `/api/app/properties/${property.id}/generate-title`, {}); return res.json(); },
    onSuccess: (data: { title: string }) => onSave({ listingTitle: data.title }),
    onError: () => toast({ title: "Failed to generate title", variant: "destructive" }),
  });

  const navBtns = (canContinue: boolean, onContinue?: () => void) => (
    <div className="flex justify-between pt-4">
      {step > 1 ? <Button variant="outline" onClick={goBack} data-testid="button-back"><ChevronLeft className="h-4 w-4 mr-1" /> Back</Button> : <div />}
      <Button onClick={onContinue || (() => goNext({}))} disabled={!canContinue} data-testid="button-continue">Continue</Button>
    </div>
  );

  return (
    <div className="bg-white border rounded-xl p-6 min-h-[280px]">
      <div className="text-xs text-gray-400 mb-4">Part 1 &middot; Step {step} of 13</div>

      {step === 1 && (
        <div className="space-y-4">
          <h3 className="text-lg font-semibold" data-testid="step-title">Confirm Your Address</h3>
          <div className="bg-gray-50 border rounded-lg p-4">
            <p className="font-medium text-gray-900" data-testid="text-confirm-address">{property.address || "No address set"}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Is there a unit ID?</label>
            <Input value={d.unitId || ""} onChange={(e) => onSave({ unitId: e.target.value })} placeholder="e.g., Unit A, Apt 2B, Suite 101" className="max-w-[300px]" data-testid="input-unit-id" />
            <p className="text-xs text-gray-400 mt-1">Leave blank if not applicable</p>
          </div>
          {navBtns(!!property.address, () => goNext({ addressConfirmed: true }))}
        </div>
      )}

      {step === 2 && (
        <div className="space-y-5">
          <h3 className="text-lg font-semibold" data-testid="step-title">Property Details</h3>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">What type of property or listing is this?</label>
            <Select value={d.listingType || ""} onValueChange={(v) => onSave({ listingType: v })}><SelectTrigger data-testid="select-listing-type"><SelectValue placeholder="Select type" /></SelectTrigger><SelectContent>{PADSPLIT_PROPERTY_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select>
          </div>
          <div><label className="block text-sm font-medium text-gray-700 mb-2">How many bedrooms? (1–15)</label><NumberStepper value={localBedrooms} onChange={handleBedroomChange} min={1} max={15} step={1} testId="stepper-bedrooms" /></div>
          <div><label className="block text-sm font-medium text-gray-700 mb-2">How many bathrooms? (half increments)</label><NumberStepper value={localBathrooms} onChange={handleBathroomChange} min={1} max={100} step={0.5} testId="stepper-bathrooms" /></div>
          <div><label className="block text-sm font-medium text-gray-700 mb-2">Are there steps inside and/or around the home?</label><YesNoToggle value={d.hasSteps ?? false} onChange={(v) => onSave({ hasSteps: v })} testId="toggle-steps" /></div>
          <div><label className="block text-sm font-medium text-gray-700 mb-2">How big is the home (in square feet)?</label><Input type="number" value={localSqftStr} onChange={(e) => handleSqftChange(e.target.value)} data-testid="input-sqft" className="max-w-[200px]" /></div>
          {navBtns(!!d.listingType)}
        </div>
      )}

      {step === 3 && (
        <div className="space-y-4">
          <h3 className="text-lg font-semibold" data-testid="step-title">Are you the owner of this property?</h3>
          <YesNoToggle value={d.isOwner} onChange={(v) => onSave({ isOwner: v })} testId="toggle-owner" />
          {navBtns(d.isOwner !== null && d.isOwner !== undefined)}
        </div>
      )}

      {step === 4 && (() => {
        const reqs = { ...DEFAULT_REQUIREMENTS, ...(d.requirements || {}) };
        const allYes = Object.values(reqs).every(v => v);
        let currentSection = "";
        return (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold" data-testid="step-title">Does your listing meet these requirements?</h3>
              <Button type="button" variant="outline" size="sm" onClick={() => { const allTrue = Object.fromEntries(Object.keys(reqs).map(k => [k, true])); onSave({ requirements: allTrue as typeof reqs }); }} data-testid="button-select-all-yes">Select All Yes</Button>
            </div>
            <div className="space-y-2">
              {REQUIREMENTS_CONFIG.map((req) => {
                const showHeader = req.section !== currentSection;
                currentSection = req.section;
                return (
                  <div key={req.key}>
                    {showHeader && <h4 className="font-semibold text-gray-800 mt-3 mb-1 text-xs uppercase tracking-wide">{req.section}</h4>}
                    <div className="flex items-center justify-between py-1.5 border-b border-gray-100">
                      <span className="text-sm text-gray-700 flex-1 pr-4">{req.label}</span>
                      <YesNoToggle value={reqs[req.key]} onChange={(v) => onSave({ requirements: { ...reqs, [req.key]: v } })} testId={`req-${req.key}`} />
                    </div>
                  </div>
                );
              })}
            </div>
            {navBtns(allYes, () => goNext({ requirements: reqs }))}
          </div>
        );
      })()}

      {step === 5 && (() => {
        const am = d.amenities || { exterior: [], communal: [], kitchen: [], bedrooms: [], safety: [] };
        return (
          <div className="space-y-5">
            <h3 className="text-lg font-semibold" data-testid="step-title">What extra features make your place stand out?</h3>
            {(Object.keys(AMENITIES_OPTIONS) as Array<keyof typeof AMENITIES_OPTIONS>).map((cat) => (
              <div key={cat}><h4 className="font-semibold text-gray-800 capitalize mb-2 text-sm">{cat}</h4><MultiSelectChips options={AMENITIES_OPTIONS[cat]} selected={am[cat] || []} onChange={(v) => onSave({ amenities: { ...am, [cat]: v } })} testId={`amenity-${cat}`} /></div>
            ))}
            {navBtns(true, () => goNext({ amenities: am }))}
          </div>
        );
      })()}

      {step === 6 && (
        <div className="space-y-5">
          <h3 className="text-lg font-semibold" data-testid="step-title">Is there a washer or dryer?</h3>
          <YesNoToggle value={d.hasWasherDryer ?? false} onChange={(v) => onSave({ hasWasherDryer: v })} testId="toggle-washer" />
          {d.hasWasherDryer && (<>
            <div><label className="block text-sm font-medium text-gray-700 mb-2">Where is it located?</label><div className="flex flex-wrap gap-2">{["In the home", "On the premise", "Off the premise"].map((opt) => <OptionButton key={opt} label={opt} selected={d.washerDryerLocation === opt} onClick={() => onSave({ washerDryerLocation: opt })} testId={`washer-loc-${opt.toLowerCase().replace(/\s+/g, "-")}`} />)}</div></div>
            <div><label className="block text-sm font-medium text-gray-700 mb-2">Do members have to pay to use it?</label><YesNoToggle value={d.washerDryerPayRequired ?? false} onChange={(v) => onSave({ washerDryerPayRequired: v })} testId="toggle-washer-pay" /></div>
          </>)}
          {navBtns(true)}
        </div>
      )}

      {step === 7 && (() => {
        const spots = d.parkingSpots || { street: 0, driveway: 0, garage: 0 };
        return (
          <div className="space-y-5">
            <h3 className="text-lg font-semibold" data-testid="step-title">Is parking available?</h3>
            <YesNoToggle value={d.parkingAvailable ?? false} onChange={(v) => onSave({ parkingAvailable: v, parkingNotes: v && !d.parkingNotes ? DEFAULT_PARKING_NOTES : d.parkingNotes })} testId="toggle-parking" />
            {d.parkingAvailable && (<>
              <div className="space-y-3"><p className="text-sm font-medium text-gray-700">How many spots per type?</p>{(["street", "driveway", "garage"] as const).map((type) => <div key={type} className="flex items-center justify-between"><span className="text-sm text-gray-700 capitalize">{type}</span><NumberStepper value={spots[type]} onChange={(v) => onSave({ parkingSpots: { ...spots, [type]: v } })} min={0} max={20} step={1} testId={`parking-${type}`} /></div>)}</div>
              <div><label className="block text-sm font-medium text-gray-700 mb-2">Notes or instructions</label><Textarea value={d.parkingNotes || ""} onChange={(e) => onSave({ parkingNotes: e.target.value })} className="min-h-[80px] text-sm" data-testid="textarea-parking-notes" /></div>
            </>)}
            {navBtns(true)}
          </div>
        );
      })()}

      {step === 8 && (
        <div className="space-y-5">
          <h3 className="text-lg font-semibold" data-testid="step-title">What about trash?</h3>
          <div><label className="block text-sm font-medium text-gray-700 mb-2">Do you have specific trash pickup days?</label><YesNoToggle value={d.hasTrashPickupDays} onChange={(v) => onSave({ hasTrashPickupDays: v })} testId="toggle-trash" /></div>
          {d.hasTrashPickupDays && (<>
            <div><label className="block text-sm font-medium text-gray-700 mb-2">Trash pickup days</label><DaySelector selected={d.trashPickupDays || []} onChange={(v) => onSave({ trashPickupDays: v })} testId="trash-days" /></div>
            <div><label className="block text-sm font-medium text-gray-700 mb-2">Do you have specific recycling pickup dates?</label><YesNoToggle value={d.hasRecyclingPickupDays ?? false} onChange={(v) => onSave({ hasRecyclingPickupDays: v })} testId="toggle-recycling" /></div>
            {d.hasRecyclingPickupDays && <div><label className="block text-sm font-medium text-gray-700 mb-2">Recycling pickup days</label><DaySelector selected={d.recyclingPickupDays || []} onChange={(v) => onSave({ recyclingPickupDays: v })} testId="recycling-days" /></div>}
            <div><label className="block text-sm font-medium text-gray-700 mb-2">How often is trash picked up?</label><div className="flex gap-2">{["Weekly", "Bi-weekly"].map((opt) => <OptionButton key={opt} label={opt} selected={d.trashPickupFrequency === opt} onClick={() => onSave({ trashPickupFrequency: opt })} testId={`trash-freq-${opt.toLowerCase()}`} />)}</div></div>
          </>)}
          <div><label className="block text-sm font-medium text-gray-700 mb-2">Add notes or instructions</label><Textarea value={d.trashNotes || ""} onChange={(e) => onSave({ trashNotes: e.target.value })} className="min-h-[60px] text-sm" data-testid="textarea-trash-notes" /></div>
          {navBtns(d.hasTrashPickupDays !== null && d.hasTrashPickupDays !== undefined)}
        </div>
      )}

      {step === 9 && (
        <div className="space-y-5">
          <h3 className="text-lg font-semibold" data-testid="step-title">What are the cleaning expectations?</h3>
          <div><label className="block text-sm font-medium text-gray-700 mb-2">Will you be restocking cleaning supplies regularly?</label><YesNoToggle value={d.restockingSupplies} onChange={(v) => onSave({ restockingSupplies: v })} testId="toggle-restock" /></div>
          <div><label className="block text-sm font-medium text-gray-700 mb-2">Will there be a professional cleaning service?</label><YesNoToggle value={d.professionalCleaning} onChange={(v) => onSave({ professionalCleaning: v })} testId="toggle-cleaning-service" /></div>
          {navBtns(d.restockingSupplies !== null && d.restockingSupplies !== undefined && d.professionalCleaning !== null && d.professionalCleaning !== undefined)}
        </div>
      )}

      {step === 10 && (
        <div className="space-y-5">
          <h3 className="text-lg font-semibold" data-testid="step-title">Describe Your Home</h3>
          <div><label className="block text-sm font-medium text-gray-700 mb-2">Select three words that describe the home</label><MultiSelectChips options={HOME_DESCRIPTION_WORDS} selected={d.homeDescriptionWords || []} onChange={(v) => onSave({ homeDescriptionWords: v })} max={3} testId="home-words" /><p className="text-xs text-gray-400 mt-1">{(d.homeDescriptionWords || []).length}/3</p></div>
          <div><label className="block text-sm font-medium text-gray-700 mb-2">Select three words that describe the location</label><MultiSelectChips options={LOCATION_DESCRIPTION_WORDS} selected={d.locationDescriptionWords || []} onChange={(v) => onSave({ locationDescriptionWords: v })} max={3} testId="location-words" /><p className="text-xs text-gray-400 mt-1">{(d.locationDescriptionWords || []).length}/3</p></div>
          {navBtns((d.homeDescriptionWords || []).length === 3 && (d.locationDescriptionWords || []).length === 3)}
        </div>
      )}

      {step === 11 && (
        <div className="space-y-4">
          <h3 className="text-lg font-semibold" data-testid="step-title">List Nearby Attractions</h3>
          <p className="text-sm text-gray-500">List nearby attractions that would interest members (optional).</p>
          <Textarea value={d.nearbyAttractions || ""} onChange={(e) => onSave({ nearbyAttractions: e.target.value })} placeholder="e.g., Walking distance to MARTA station, 5 min from Kroger..." className="min-h-[100px] text-sm" data-testid="textarea-attractions" />
          {navBtns(true)}
        </div>
      )}

      {step === 12 && (
        <div className="space-y-4">
          <h3 className="text-lg font-semibold" data-testid="step-title">Listing Description</h3>
          <p className="text-sm text-gray-500">We've generated an AI listing description. Customize however you want.</p>
          <Button variant="outline" size="sm" onClick={() => generateListingMutation.mutate()} disabled={generateListingMutation.isPending} data-testid="button-generate-description">
            {generateListingMutation.isPending ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Generating...</> : <><Sparkles className="h-4 w-4 mr-2" /> Generate AI Description</>}
          </Button>
          <Textarea value={d.listingDescription || property.description || ""} onChange={(e) => onSave({ listingDescription: e.target.value })} className="min-h-[160px] text-sm" data-testid="textarea-listing-description" />
          {navBtns(!!(d.listingDescription || property.description))}
        </div>
      )}

      {step === 13 && (
        <div className="space-y-4">
          <h3 className="text-lg font-semibold" data-testid="step-title">Listing Title</h3>
          <p className="text-sm text-gray-500">We've also helped with the title. Feel free to customize.</p>
          <Button variant="outline" size="sm" onClick={() => generateTitleMutation.mutate()} disabled={generateTitleMutation.isPending} data-testid="button-generate-title">
            {generateTitleMutation.isPending ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Generating...</> : <><Sparkles className="h-4 w-4 mr-2" /> Generate AI Title</>}
          </Button>
          <div><Input value={d.listingTitle || ""} onChange={(e) => onSave({ listingTitle: e.target.value.substring(0, 100) })} maxLength={100} placeholder="Enter listing title..." data-testid="input-listing-title" /><p className="text-xs text-gray-400 mt-1">{(d.listingTitle || "").length}/100 characters</p></div>
          <div className="flex justify-between pt-4">
            <Button variant="outline" onClick={goBack} data-testid="button-back"><ChevronLeft className="h-4 w-4 mr-1" /> Back</Button>
            <Button onClick={() => onSave({ part1Complete: true, currentPart: 2, currentStep: 1 })} disabled={!d.listingTitle} data-testid="button-complete-part1" className="bg-green-600 hover:bg-green-700"><CheckCircle2 className="h-4 w-4 mr-2" /> Complete Part 1</Button>
          </div>
        </div>
      )}
    </div>
  );
}

function Part2Wizard({ property, wizardData, onSave }: { property: Property; wizardData: WizardData; onSave: (data: Partial<WizardData>) => void }) {
  const [step, setStep] = useState(wizardData.currentPart === 2 ? (wizardData.currentStep || 1) : 1);
  const d = wizardData;
  const bedroomCount = d.bedroomCount || property.roomCount || 2;
  const [bedroomReqs, setBedroomReqs] = useState<boolean[]>(Array(BEDROOM_REQUIREMENTS.length).fill(false));

  const goNext = (updates: Partial<WizardData>) => { const ns = step + 1; onSave({ ...updates, currentStep: ns, currentPart: 2 }); setStep(ns); };
  const goBack = () => { if (step === 1) { onSave({ currentPart: 1, currentStep: 13 }); return; } const ps = Math.max(1, step - 1); onSave({ currentStep: ps, currentPart: 2 }); setStep(ps); };

  const existingDetails = d.bedroomDetails || [];
  const getDetail = (idx: number): BedroomDetail => existingDetails[idx] || {
    roomIndex: idx, floorLevel: "", stairsRequired: false, roomSize: "", bedSize: "", occupants: "", bathroomType: "",
    closetType: "", windowCount: 1, doorLockType: "", smartLockRemote: false, smartLockCode: "", smartLockAddLater: false,
    climateControl: "", bedroomLabelPhoto: "", useComingSoonLabel: false, uniqueFeatures: [], roomDescription: "",
    weeklyPrice: "180", moveInFee: d.defaultMoveInFee || "100",
  };

  const updateDetail = (idx: number, updates: Partial<BedroomDetail>) => {
    const newDetails = [...existingDetails];
    while (newDetails.length <= idx) newDetails.push(getDetail(newDetails.length));
    newDetails[idx] = { ...newDetails[idx], ...updates };
    onSave({ bedroomDetails: newDetails });
  };

  const totalSteps = 1 + bedroomCount;

  return (
    <div className="bg-white border rounded-xl p-6 min-h-[280px]">
      <div className="text-xs text-gray-400 mb-4">Part 2 &middot; Step {step} of {totalSteps}</div>

      {step === 1 && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold" data-testid="step-title">Does each bedroom have the following?</h3>
            <Button type="button" variant="outline" size="sm" onClick={() => setBedroomReqs(Array(BEDROOM_REQUIREMENTS.length).fill(true))} data-testid="button-select-all-bedroom-yes">Select All Yes</Button>
          </div>
          <p className="text-sm text-gray-500">All items must be confirmed before proceeding.</p>
          <div className="space-y-2">
            {BEDROOM_REQUIREMENTS.map((req, i) => (
              <div key={i} className="flex items-center gap-3 py-1.5 border-b border-gray-100">
                <button type="button" className={`w-5 h-5 rounded border flex items-center justify-center transition-colors ${bedroomReqs[i] ? "bg-green-600 border-green-600 text-white" : "border-gray-300 hover:border-gray-400"}`} onClick={() => { const n = [...bedroomReqs]; n[i] = !n[i]; setBedroomReqs(n); }} data-testid={`bedroom-req-${i}`}>
                  {bedroomReqs[i] && <CheckCircle2 className="h-3.5 w-3.5" />}
                </button>
                <span className="text-sm text-gray-700">{req}</span>
              </div>
            ))}
          </div>
          <div><label className="block text-sm font-medium text-gray-700 mb-2">Default move-in fee per room ($)</label><Input type="number" value={d.defaultMoveInFee || "100"} onChange={(e) => onSave({ defaultMoveInFee: e.target.value })} className="max-w-[200px]" data-testid="input-default-move-in-fee" /></div>
          <div className="flex justify-between pt-4">
            <Button variant="outline" onClick={goBack} data-testid="button-back"><ChevronLeft className="h-4 w-4 mr-1" /> Back</Button>
            <Button onClick={() => goNext({ bedroomRequirementsConfirmed: true })} disabled={!bedroomReqs.every(v => v)} data-testid="button-got-it">Got it</Button>
          </div>
        </div>
      )}

      {step >= 2 && step <= totalSteps && (() => {
        const idx = step - 2;
        const detail = getDetail(idx);
        const isLast = idx === bedroomCount - 1;
        const isDetailComplete = !!(detail.floorLevel && detail.roomSize && detail.bedSize && detail.occupants && detail.bathroomType && detail.closetType && detail.doorLockType && detail.climateControl && detail.weeklyPrice && (detail.bedroomLabelPhoto || detail.useComingSoonLabel));

        return (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold flex items-center gap-2" data-testid="step-title"><BedDouble className="h-5 w-5 text-blue-600" /> Bedroom {idx + 1}</h3>

            <div><label className="block text-sm font-medium text-gray-700 mb-2">Floor level</label><div className="flex flex-wrap gap-2">{["Basement", "Ground", "First Floor", "Second Floor", "Above Second Floor"].map((o) => <OptionButton key={o} label={o} selected={detail.floorLevel === o} onClick={() => updateDetail(idx, { floorLevel: o })} testId={`floor-${o.toLowerCase().replace(/\s+/g, "-")}`} />)}</div></div>

            <div><label className="block text-sm font-medium text-gray-700 mb-2">Are stairs required?</label><YesNoToggle value={detail.stairsRequired} onChange={(v) => updateDetail(idx, { stairsRequired: v })} testId={`br-${idx}-stairs`} /></div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Room size (based on shortest wall)</label>
              <div className="flex flex-wrap gap-2">{[{ v: "Cozy", l: "Cozy (< 8 ft)" }, { v: "Standard", l: "Standard (8–11 ft)" }, { v: "Large", l: "Large (> 11 ft)" }].map((o) => <OptionButton key={o.v} label={o.l} selected={detail.roomSize === o.v} onClick={() => updateDetail(idx, { roomSize: o.v })} testId={`room-size-${o.v.toLowerCase()}`} />)}</div>
              <div className="flex items-center gap-2 mt-2">
                <label className="text-xs text-gray-500 shrink-0">Exact dimensions (optional):</label>
                <Input
                  value={detail.widthFt || ""}
                  onChange={(e) => updateDetail(idx, { widthFt: e.target.value })}
                  placeholder="W ft"
                  className="text-xs h-7 w-16 px-1.5"
                  data-testid={`input-launch-room-width-${idx}`}
                />
                <span className="text-xs text-gray-400">×</span>
                <Input
                  value={detail.lengthFt || ""}
                  onChange={(e) => updateDetail(idx, { lengthFt: e.target.value })}
                  placeholder="L ft"
                  className="text-xs h-7 w-16 px-1.5"
                  data-testid={`input-launch-room-length-${idx}`}
                />
                {detail.widthFt && detail.lengthFt && !isNaN(parseFloat(detail.widthFt)) && !isNaN(parseFloat(detail.lengthFt)) && (
                  <span className="text-xs text-muted-foreground" data-testid={`text-launch-sqft-${idx}`}>
                    = {(parseFloat(detail.widthFt) * parseFloat(detail.lengthFt)).toFixed(1)} sq ft
                  </span>
                )}
              </div>
              {!detail.widthFt && !detail.lengthFt && detail.roomSize && (
                <p className="text-[10px] text-muted-foreground italic mt-1" data-testid={`text-launch-size-proxy-${idx}`}>
                  Using PadSplit size ({detail.roomSize}) as analytics proxy — add dimensions for sq-ft grouping
                </p>
              )}
            </div>

            <div><label className="block text-sm font-medium text-gray-700 mb-2">Bed size</label><div className="flex flex-wrap gap-2">{["Twin", "Twin XL", "Full", "Queen", "King"].map((o) => <OptionButton key={o} label={o} selected={detail.bedSize === o} onClick={() => updateDetail(idx, { bedSize: o })} testId={`bed-${o.toLowerCase().replace(/\s+/g, "-")}`} />)}</div></div>

            <div><label className="block text-sm font-medium text-gray-700 mb-2">Occupants</label><div className="flex gap-2">{["Single Occupant", "Multiple Occupants"].map((o) => <OptionButton key={o} label={o} selected={detail.occupants === o} onClick={() => updateDetail(idx, { occupants: o })} testId={`occ-${o.toLowerCase().replace(/\s+/g, "-")}`} />)}</div></div>

            <div><label className="block text-sm font-medium text-gray-700 mb-2">Bathroom type</label><div className="flex flex-wrap gap-2">{["Jack and Jill", "Half Private", "Full Private"].map((o) => <OptionButton key={o} label={o} selected={detail.bathroomType === o} onClick={() => updateDetail(idx, { bathroomType: o })} testId={`bath-${o.toLowerCase().replace(/\s+/g, "-")}`} />)}</div></div>

            <div><label className="block text-sm font-medium text-gray-700 mb-2">Closet type</label><div className="flex flex-wrap gap-2">{["Wardrobe or Dresser", "Reach-in Closet", "Walk-in Closet"].map((o) => <OptionButton key={o} label={o} selected={detail.closetType === o} onClick={() => updateDetail(idx, { closetType: o })} testId={`closet-${o.toLowerCase().replace(/\s+/g, "-")}`} />)}</div></div>

            <div><label className="block text-sm font-medium text-gray-700 mb-2">Windows</label><NumberStepper value={detail.windowCount || 1} onChange={(v) => updateDetail(idx, { windowCount: v })} min={1} max={10} step={1} testId={`br-${idx}-windows`} /></div>

            <div><label className="block text-sm font-medium text-gray-700 mb-2">Door lock type</label><div className="flex flex-wrap gap-2">{["Privacy", "Keyed", "Punch Code", "Smart"].map((o) => <OptionButton key={o} label={o} selected={detail.doorLockType === o} onClick={() => updateDetail(idx, { doorLockType: o })} testId={`lock-${o.toLowerCase().replace(/\s+/g, "-")}`} />)}</div></div>
            {detail.doorLockType === "Smart" && (
              <div className="pl-4 border-l-2 border-blue-200 space-y-3">
                <div><label className="block text-sm font-medium text-gray-700 mb-2">Remote lock?</label><YesNoToggle value={detail.smartLockRemote} onChange={(v) => updateDetail(idx, { smartLockRemote: v })} testId={`br-${idx}-remote`} /></div>
                {!detail.smartLockAddLater && <div><label className="block text-sm font-medium text-gray-700 mb-2">Lock code</label><Input value={detail.smartLockCode || ""} onChange={(e) => updateDetail(idx, { smartLockCode: e.target.value })} placeholder="Enter code" className="max-w-[200px]" data-testid={`input-lock-code-${idx}`} /></div>}
                <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer"><input type="checkbox" checked={detail.smartLockAddLater} onChange={(e) => updateDetail(idx, { smartLockAddLater: e.target.checked })} data-testid={`checkbox-lock-later-${idx}`} /> I'll add this later</label>
              </div>
            )}

            <div><label className="block text-sm font-medium text-gray-700 mb-2">Climate control</label><div className="flex flex-wrap gap-2">{["Central AC", "Window AC", "Mini-split", "None"].map((o) => <OptionButton key={o} label={o} selected={detail.climateControl === o} onClick={() => updateDetail(idx, { climateControl: o })} testId={`climate-${o.toLowerCase().replace(/\s+/g, "-")}`} />)}</div></div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Bedroom label photo</label>
              {detail.bedroomLabelPhoto ? (
                <div className="flex items-center gap-3"><img src={detail.bedroomLabelPhoto} alt="Label" className="w-16 h-16 rounded border object-cover" /><Button variant="outline" size="sm" onClick={() => updateDetail(idx, { bedroomLabelPhoto: "" })}>Remove</Button></div>
              ) : (
                <PhotoUploader photos={[]} onUpload={(url) => updateDetail(idx, { bedroomLabelPhoto: url, useComingSoonLabel: false })} onRemove={() => {}} testId={`br-${idx}-label`} />
              )}
              <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer mt-2"><input type="checkbox" checked={detail.useComingSoonLabel} onChange={(e) => updateDetail(idx, { useComingSoonLabel: e.target.checked, bedroomLabelPhoto: e.target.checked ? "" : detail.bedroomLabelPhoto })} data-testid={`checkbox-coming-soon-${idx}`} /> Use "Coming Soon" default</label>
            </div>

            <div><label className="block text-sm font-medium text-gray-700 mb-2">Unique features</label><MultiSelectChips options={["Private Entrance", "Mini Fridge", "Workspace", "Ceiling Fan", "Seating", "Extra Storage"]} selected={detail.uniqueFeatures || []} onChange={(v) => updateDetail(idx, { uniqueFeatures: v })} testId={`br-${idx}-features`} /></div>

            <div><label className="block text-sm font-medium text-gray-700 mb-2">Room description</label><Textarea value={detail.roomDescription || ""} onChange={(e) => updateDetail(idx, { roomDescription: e.target.value })} placeholder="Describe this room..." className="min-h-[60px] text-sm" data-testid={`textarea-room-desc-${idx}`} /></div>

            <div className="bg-gray-50 border rounded-lg p-4 space-y-3">
              <h4 className="font-semibold text-gray-800 text-sm">Set the price for Bedroom {idx + 1}</h4>
              <div className="grid grid-cols-2 gap-4">
                <div><label className="block text-xs font-medium text-gray-700 mb-1">Weekly room rate ($)</label><NumberStepper value={parseInt(detail.weeklyPrice) || 180} onChange={(v) => updateDetail(idx, { weeklyPrice: String(v) })} min={100} max={1000} step={5} testId={`br-${idx}-price`} /></div>
                <div><label className="block text-xs font-medium text-gray-700 mb-1">Move-in fee ($)</label><Input type="number" value={detail.moveInFee || d.defaultMoveInFee || "100"} onChange={(e) => updateDetail(idx, { moveInFee: e.target.value })} className="h-9" data-testid={`input-movein-${idx}`} /></div>
              </div>
            </div>

            <div className="flex justify-between pt-4">
              <Button variant="outline" onClick={goBack} data-testid="button-back"><ChevronLeft className="h-4 w-4 mr-1" /> Back</Button>
              {isLast ? (
                <Button onClick={() => onSave({ part2Complete: true, currentPart: 3, currentStep: 1 })} disabled={!isDetailComplete} data-testid="button-finish" className="bg-green-600 hover:bg-green-700"><CheckCircle2 className="h-4 w-4 mr-2" /> Finish</Button>
              ) : (
                <Button onClick={() => goNext({})} disabled={!isDetailComplete} data-testid="button-continue">Continue</Button>
              )}
            </div>
          </div>
        );
      })()}
    </div>
  );
}

function Part3Wizard({ property, wizardData, onSave }: { property: Property; wizardData: WizardData; onSave: (data: Partial<WizardData>) => void }) {
  const d = wizardData;
  const bedroomCount = d.bedroomCount || property.roomCount || 2;
  const bathroomCount = Math.floor(d.bathroomCount || property.bathroomCount || 1);
  const photos: PhotoSection = d.photos || { exterior: [], commonSpaces: [], bedrooms: {}, bathrooms: {}, other: [], videoTourUrl: "", threeDTourUrl: "", bestPhotosFirst: false };

  const roomsQuery = useQuery<Room[]>({
    queryKey: ["/api/app/properties", property.id, "rooms"],
    queryFn: async () => {
      const res = await fetch(`/api/app/properties/${property.id}/rooms`, { credentials: "include" });
      if (!res.ok) return [];
      return res.json();
    },
  });

  const prefillApplied = useRef(false);
  useEffect(() => {
    const roomsData = roomsQuery.data;
    if (!roomsData || prefillApplied.current) return;
    const bedroomRooms = roomsData.filter(r => (r.roomType || "bedroom") === "bedroom");
    const bathroomRooms = roomsData.filter(r => r.roomType === "bathroom");
    if (bathroomRooms.length === 0 || bedroomRooms.length === 0) return;
    // Index contract: the wizard assigns bathroom slot `bIdx` by the same
    // filtered position that `bathroomRooms` uses here — both derive from
    // the API rooms array filtered to roomType === "bathroom" in the same
    // insertion order. The wizard and this prefill share this ordering
    // assumption. If bathroom ordering ever diverges, prefill must be updated
    // to key on stable bathroom room IDs rather than positional indices.
    const bedroomIndexById: Record<number, number> = {};
    bedroomRooms.forEach((br, idx) => { bedroomIndexById[br.id] = idx; });
    let needsUpdate = false;
    const updatedBathrooms: PhotoSection["bathrooms"] = { ...photos.bathrooms };
    bathroomRooms.forEach((bath, bIdx) => {
      const existingAssigned = updatedBathrooms[bIdx]?.assignedBedrooms;
      if (existingAssigned && existingAssigned.length > 0) return;
      const roomIds: number[] = (bath.associatedBedroomIds as number[] | null) || [];
      if (roomIds.length === 0) return;
      const indices = roomIds.map(id => bedroomIndexById[id]).filter((idx): idx is number => idx !== undefined);
      if (indices.length === 0) return;
      updatedBathrooms[bIdx] = { ...(updatedBathrooms[bIdx] || { photos: [] }), assignedBedrooms: indices };
      needsUpdate = true;
    });
    // Mark prefill as applied after the first attempt regardless of whether any
    // slots were filled. This prevents re-applying on subsequent refetches, which
    // would override intentional manual changes (including clearing all bedrooms).
    prefillApplied.current = true;
    if (needsUpdate) {
      onSave({ photos: { ...photos, bathrooms: updatedBathrooms } });
    }
  }, [roomsQuery.data]);

  const updatePhotos = (updates: Partial<PhotoSection>) => onSave({ photos: { ...photos, ...updates } });

  const addToSection = (section: "exterior" | "commonSpaces" | "other", url: string) => updatePhotos({ [section]: [...(photos[section] || []), url] });
  const removeFromSection = (section: "exterior" | "commonSpaces" | "other", idx: number) => updatePhotos({ [section]: (photos[section] || []).filter((_: string, i: number) => i !== idx) });

  const addBedroomPhoto = (brIdx: number, url: string) => { const br = { ...photos.bedrooms }; br[brIdx] = [...(br[brIdx] || []), url]; updatePhotos({ bedrooms: br }); };
  const removeBedroomPhoto = (brIdx: number, pIdx: number) => { const br = { ...photos.bedrooms }; br[brIdx] = (br[brIdx] || []).filter((_: string, i: number) => i !== pIdx); updatePhotos({ bedrooms: br }); };

  const addBathroomPhoto = (bIdx: number, url: string) => { const ba = { ...photos.bathrooms }; if (!ba[bIdx]) ba[bIdx] = { photos: [], assignedBedrooms: [] }; ba[bIdx] = { ...ba[bIdx], photos: [...ba[bIdx].photos, url] }; updatePhotos({ bathrooms: ba }); };
  const removeBathroomPhoto = (bIdx: number, pIdx: number) => { const ba = { ...photos.bathrooms }; if (ba[bIdx]) ba[bIdx] = { ...ba[bIdx], photos: ba[bIdx].photos.filter((_: string, i: number) => i !== pIdx) }; updatePhotos({ bathrooms: ba }); };
  const assignBathroomBedroom = (bIdx: number, bedrooms: number[]) => { const ba = { ...photos.bathrooms }; if (!ba[bIdx]) ba[bIdx] = { photos: [], assignedBedrooms: [] }; ba[bIdx] = { ...ba[bIdx], assignedBedrooms: bedrooms }; updatePhotos({ bathrooms: ba }); };

  const goBack = () => onSave({ currentPart: 2, currentStep: (d.bedroomCount || 2) + 1 });

  return (
    <div className="bg-white border rounded-xl p-6 min-h-[280px]">
      <h3 className="text-lg font-semibold mb-1" data-testid="step-title">Share photos of your space</h3>
      <p className="text-sm text-gray-500 mb-4">Add photos of your property and preview listing card.</p>

      <label className="flex items-center gap-2 text-sm text-gray-600 mb-6 cursor-pointer">
        <input type="checkbox" checked={photos.bestPhotosFirst} onChange={(e) => updatePhotos({ bestPhotosFirst: e.target.checked })} data-testid="toggle-best-photos" />
        Best photos first
      </label>

      <div className="space-y-6">
        <div>
          <h4 className="font-semibold text-gray-800 text-sm mb-2 flex items-center gap-2"><Camera className="h-4 w-4" /> Exterior</h4>
          <PhotoUploader photos={photos.exterior || []} onUpload={(url) => addToSection("exterior", url)} onRemove={(i) => removeFromSection("exterior", i)} testId="photos-exterior" />
        </div>

        <div>
          <h4 className="font-semibold text-gray-800 text-sm mb-2">Common Spaces (including Kitchen)</h4>
          <PhotoUploader photos={photos.commonSpaces || []} onUpload={(url) => addToSection("commonSpaces", url)} onRemove={(i) => removeFromSection("commonSpaces", i)} testId="photos-common" />
        </div>

        <div>
          <h4 className="font-semibold text-gray-800 text-sm mb-3">Bedroom Photos</h4>
          <div className="space-y-3">
            {Array.from({ length: bedroomCount }, (_, i) => (
              <div key={i} className="border rounded-lg p-3">
                <h5 className="text-sm font-medium text-gray-700 mb-2">Bedroom {i + 1}</h5>
                <PhotoUploader photos={photos.bedrooms?.[i] || []} onUpload={(url) => addBedroomPhoto(i, url)} onRemove={(pi) => removeBedroomPhoto(i, pi)} testId={`photos-bedroom-${i}`} />
              </div>
            ))}
          </div>
        </div>

        <div>
          <h4 className="font-semibold text-gray-800 text-sm mb-3">Bathroom Photos</h4>
          <div className="space-y-3">
            {Array.from({ length: bathroomCount }, (_, i) => (
              <div key={i} className="border rounded-lg p-3">
                <h5 className="text-sm font-medium text-gray-700 mb-2">Bathroom {i + 1}</h5>
                <div className="mb-2">
                  <label className="block text-xs text-gray-600 mb-1">Assigned bedrooms</label>
                  <div className="flex flex-wrap gap-1">
                    {Array.from({ length: bedroomCount }, (_, bi) => {
                      const assigned = photos.bathrooms?.[i]?.assignedBedrooms || [];
                      const isAssigned = assigned.includes(bi);
                      return <button key={bi} type="button" className={`px-2 py-0.5 rounded text-xs border ${isAssigned ? "bg-blue-600 text-white border-blue-600" : "bg-white text-gray-600 border-gray-300"}`} onClick={() => assignBathroomBedroom(i, isAssigned ? assigned.filter((x: number) => x !== bi) : [...assigned, bi])} data-testid={`bath-${i}-assign-br-${bi}`}>BR {bi + 1}</button>;
                    })}
                  </div>
                </div>
                <PhotoUploader photos={photos.bathrooms?.[i]?.photos || []} onUpload={(url) => addBathroomPhoto(i, url)} onRemove={(pi) => removeBathroomPhoto(i, pi)} testId={`photos-bathroom-${i}`} />
              </div>
            ))}
          </div>
        </div>

        <div>
          <h4 className="font-semibold text-gray-800 text-sm mb-1">Other Photos (Optional)</h4>
          <p className="text-xs text-gray-500 mb-2">Photos of the community and nearby attractions such as grocery stores, transit stops, and gyms.</p>
          <PhotoUploader photos={photos.other || []} onUpload={(url) => addToSection("other", url)} onRemove={(i) => removeFromSection("other", i)} testId="photos-other" />
        </div>

        <div className="border-t pt-4 space-y-4">
          <div>
            <h4 className="font-semibold text-gray-800 text-sm mb-1 flex items-center gap-2"><Video className="h-4 w-4" /> Video Tour (Optional)</h4>
            <p className="text-xs text-gray-500 mb-2">For best results, use YouTube, Vimeo, Dropbox, or Google Drive.</p>
            <Input value={photos.videoTourUrl || ""} onChange={(e) => updatePhotos({ videoTourUrl: e.target.value })} placeholder="Paste video link..." data-testid="input-video-tour" />
            <div className="bg-amber-50 border border-amber-200 rounded p-3 mt-2 text-xs text-amber-800">
              <p className="font-semibold mb-1">Guidelines:</p>
              <ul className="list-disc pl-4 space-y-0.5">
                <li>Do not expose your property's address or location</li>
                <li>Portrait orientation recommended for mobile browsing</li>
                <li>Record when fully furnished and all rooms vacant</li>
                <li>Walk and talk through the home like a tour</li>
                <li>Hit all highlights: bedrooms, shared spaces, amenities, parking</li>
              </ul>
            </div>
          </div>
          <div>
            <h4 className="font-semibold text-gray-800 text-sm mb-1">3D Tour (Optional)</h4>
            <Input value={photos.threeDTourUrl || ""} onChange={(e) => updatePhotos({ threeDTourUrl: e.target.value })} placeholder="Paste 3D tour URL..." data-testid="input-3d-tour" />
          </div>
        </div>
      </div>

      <div className="flex justify-between pt-6">
        <Button variant="outline" onClick={goBack} data-testid="button-back"><ChevronLeft className="h-4 w-4 mr-1" /> Back</Button>
        <Button onClick={() => onSave({ part3Complete: true, currentPart: 4, currentStep: 1 })} disabled={(photos.exterior || []).length === 0} data-testid="button-complete-part3" className="bg-green-600 hover:bg-green-700"><CheckCircle2 className="h-4 w-4 mr-2" /> Complete Part 3</Button>
      </div>
    </div>
  );
}

function Part4Wizard({ property, wizardData, onSave }: { property: Property; wizardData: WizardData; onSave: (data: Partial<WizardData>) => void }) {
  const d = wizardData;
  const goBack = () => onSave({ currentPart: 3, currentStep: 1 });

  return (
    <div className="bg-white border rounded-xl p-6 min-h-[280px]">
      <h3 className="text-lg font-semibold mb-1 flex items-center gap-2" data-testid="step-title"><Settings2 className="h-5 w-5 text-blue-600" /> Finishing Setup</h3>
      <p className="text-sm text-gray-500 mb-6">Add moving instructions, lock codes, and set up your payout.</p>

      <div className="space-y-5">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Moving instructions for new members</label>
          <Textarea value={d.movingInstructions || ""} onChange={(e) => onSave({ movingInstructions: e.target.value })} placeholder="Describe how new members should move in, where to find keys, parking for move-in day, etc." className="min-h-[100px] text-sm" data-testid="textarea-moving-instructions" />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Lock codes summary</label>
          <Textarea value={d.lockCodes || ""} onChange={(e) => onSave({ lockCodes: e.target.value })} placeholder="Front door: 1234, Back door: 5678..." className="min-h-[80px] text-sm" data-testid="textarea-lock-codes" />
        </div>

        <div className="bg-gray-50 border rounded-lg p-4">
          <h4 className="font-semibold text-gray-800 text-sm mb-1">Payout Setup</h4>
          <p className="text-sm text-gray-500">Payout configuration will be handled through PadSplit directly after launch.</p>
        </div>
      </div>

      <div className="flex justify-between pt-6">
        <Button variant="outline" onClick={goBack} data-testid="button-back"><ChevronLeft className="h-4 w-4 mr-1" /> Back</Button>
        <Button onClick={() => onSave({ part4Complete: true })} disabled={!d.movingInstructions} data-testid="button-complete-part4" className="bg-green-600 hover:bg-green-700"><CheckCircle2 className="h-4 w-4 mr-2" /> Complete Part 4</Button>
      </div>
    </div>
  );
}

function PropertyLaunchDetail({ propertyId }: { propertyId: number }) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [, navigate] = useLocation();

  const propertyQuery = useQuery<Property>({
    queryKey: ["/api/app/properties", propertyId],
    queryFn: async () => {
      const res = await fetch(`/api/app/properties/${propertyId}`, { credentials: "include" });
      if (!res.ok) throw new Error("Not found");
      return res.json();
    },
  });
  usePageTitle(propertyQuery.data?.address || "Property Launch");

  const imageRowsQuery = useQuery<Room[]>({
    queryKey: ["/api/app/properties", propertyId, "rooms"],
    queryFn: async () => {
      const res = await fetch(`/api/app/properties/${propertyId}/rooms`, { credentials: "include" });
      if (!res.ok) return [];
      return res.json();
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (data: Record<string, unknown>) => {
      const res = await apiRequest("PATCH", `/api/app/properties/${propertyId}`, data);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/properties", propertyId] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/properties"] });
    },
    onError: () => toast({ title: "Failed to save", variant: "destructive" }),
  });

  const launchMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", `/api/app/properties/${propertyId}/launch`, {});
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/properties", propertyId] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/properties"] });
      toast({ title: "Property launched on PadSplit!", description: "The launch intent has been recorded." });
      navigate("/app/property-launch");
    },
    onError: () => toast({ title: "Launch failed", variant: "destructive" }),
  });

  if (propertyQuery.isLoading) return <div className="space-y-4"><Skeleton className="h-8 w-64" /><Skeleton className="h-[400px] w-full" /></div>;

  const property = propertyQuery.data;
  if (!property) return <div className="text-center py-20"><p className="text-gray-500">Property not found</p><Button variant="ghost" asChild data-testid="button-back-to-launch"><Link href="/app/property-launch">Back</Link></Button></div>;

  const wizardData: WizardData = (property.launchWizardData as WizardData) || {};
  const currentPart = wizardData.currentPart || 1;
  const alreadyLaunched = !!property.launchedAt;
  const imageRows = imageRowsQuery.data || [];

  const saveWizardData = (updates: Partial<WizardData>) => {
    const newData = { ...wizardData, ...updates };
    const syncFields: Record<string, unknown> = { launchWizardData: newData };
    if (updates.listingType) syncFields.padsplitPropertyType = updates.listingType;
    if (updates.isOwner !== undefined && updates.isOwner !== null) syncFields.ownerOccupancy = updates.isOwner;
    if (updates.part1Complete) {
      syncFields.minWeeklyPrice = newData.bedroomDetails?.[0]?.weeklyPrice || "180";
      syncFields.maxWeeklyPrice = newData.bedroomDetails?.[newData.bedroomDetails.length - 1]?.weeklyPrice || "260";
    }
    // Derive and persist sizeCategory + room dimensions to individual room records
    // whenever bedroomDetails changes. The host-selected roomSize ("Cozy" / "Standard" /
    // "Large") maps directly to PadSplit's size-category taxonomy; widthFt × lengthFt
    // gives the exact squareFootage for Phase 2 analytics grouping.
    if (updates.bedroomDetails) {
      const bedroomRooms = (imageRowsQuery.data || [])
        .filter(r => (r.roomType || "bedroom") === "bedroom" && !r.shadowOfRoomId)
        .sort((a, b) =>
          (a.roomNumber ?? "").localeCompare(b.roomNumber ?? "", undefined, { numeric: true }),
        );
      (newData.bedroomDetails || []).forEach((detail, idx) => {
        const room = bedroomRooms[idx];
        if (!room) return;
        const patch: Record<string, unknown> = {};
        if (detail.roomSize) patch.sizeCategory = detail.roomSize.toLowerCase();
        const w = detail.widthFt ? parseFloat(detail.widthFt) : NaN;
        const l = detail.lengthFt ? parseFloat(detail.lengthFt) : NaN;
        if (detail.widthFt !== undefined) patch.widthFt = detail.widthFt || null;
        if (detail.lengthFt !== undefined) patch.lengthFt = detail.lengthFt || null;
        if (!isNaN(w) && !isNaN(l) && w > 0 && l > 0) {
          patch.squareFootage = String(w * l);
        } else if (detail.widthFt !== undefined || detail.lengthFt !== undefined) {
          // Explicitly clear stale sq-ft if either dimension is removed or invalid.
          patch.squareFootage = null;
        }
        if (Object.keys(patch).length > 0) {
          // Fire-and-forget: best-effort analytics enrichment; does not block the wizard save.
          apiRequest("PATCH", `/api/app/rooms/${room.id}`, patch).catch((err: unknown) => {
            console.warn(`[launch-wizard] room ${room.id} dimension sync failed`, err);
          });
        }
      });
    }
    updateMutation.mutate(syncFields);
  };

  const onSaveField = (field: string, value: unknown) => updateMutation.mutate({ [field]: value });

  const handlePartClick = (p: number) => {
    saveWizardData({ currentPart: p, currentStep: 1 });
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 text-sm text-gray-500 mb-2">
        <Link href="/app/property-launch"><span className="hover:text-gray-700 cursor-pointer" data-testid="link-breadcrumb-launch">Property Launch</span></Link>
        <ChevronRight className="h-3 w-3" />
        <span className="text-gray-900" data-testid="text-breadcrumb-property">{property.name}</span>
      </div>

      <div className="flex items-start justify-between mb-2">
        <div>
          <h2 className="text-xl font-bold text-gray-900" data-testid="text-property-launch-title" data-tour="property-launch-heading">{property.name}</h2>
          {property.address && <p className="text-sm text-gray-500 mt-0.5" data-testid="text-property-launch-address">{property.address}</p>}
          <p className="text-xs text-blue-600 mt-1">Make sure your utilities, including Wi-Fi, are set up before starting this section.</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {alreadyLaunched && <Badge className="bg-green-100 text-green-700" data-testid="badge-launched">Launched</Badge>}
          <ExplainerVideoButton pageKey="property-launch" />
          <GuideButton pageKey="property-launch" />
        </div>
      </div>
      <GuideNudgeBanner pageKey="property-launch" />

      {!alreadyLaunched && (
        <PartHeader currentPart={currentPart} part1Complete={!!wizardData.part1Complete} part2Complete={!!wizardData.part2Complete} part3Complete={!!wizardData.part3Complete} part4Complete={!!wizardData.part4Complete} onPartClick={handlePartClick} />
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          {alreadyLaunched ? (
            <div className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-lg p-4" data-testid="banner-already-launched">
              <CheckCircle2 className="h-5 w-5 text-green-500 shrink-0" />
              <div>
                <p className="text-sm font-semibold text-green-800">Property launched on PadSplit</p>
                <p className="text-xs text-green-600">Launched on {new Date(property.launchedAt!).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}.</p>
              </div>
            </div>
          ) : (
            <>
              {currentPart === 1 && <Part1Wizard property={property} wizardData={wizardData} onSave={saveWizardData} />}
              {currentPart === 2 && wizardData.part1Complete && <Part2Wizard property={property} wizardData={wizardData} onSave={saveWizardData} />}
              {currentPart === 3 && wizardData.part2Complete && <Part3Wizard property={property} wizardData={wizardData} onSave={saveWizardData} />}
              {currentPart === 4 && wizardData.part3Complete && <Part4Wizard property={property} wizardData={wizardData} onSave={saveWizardData} />}

              {wizardData.part4Complete && (() => {
                const checklist = buildChecklist(property, imageRows);
                const allChecklistComplete = checklist.every(c => c.complete);
                return (
                  <div className="mt-6 space-y-2">
                    {!allChecklistComplete && (
                      <p className="text-sm text-amber-600 flex items-center gap-1.5"><AlertCircle className="h-4 w-4" /> Complete all 6 checklist items in the sidebar before launching.</p>
                    )}
                    <Button className="w-full bg-brand hover:bg-brand-hover" disabled={launchMutation.isPending || !allChecklistComplete} onClick={() => launchMutation.mutate()} data-testid="button-launch-padsplit-main">
                      <Rocket className="h-4 w-4 mr-2" />
                      {launchMutation.isPending ? "Launching..." : allChecklistComplete ? "Launch Property on PadSplit" : `Checklist incomplete (${checklist.filter(c => c.complete).length}/6)`}
                    </Button>
                  </div>
                );
              })()}
            </>
          )}
        </div>

        <div className="space-y-4">
          
          <FurnishingProgress rooms={imageRows} isLoading={imageRowsQuery.isLoading} />
          <LaunchChecklist property={property} rooms={imageRows} onLaunch={() => launchMutation.mutate()} launchPending={launchMutation.isPending} />
          <div className="border rounded-lg p-4">
            <h3 className="font-semibold text-gray-900 text-sm mb-2">Quick Links</h3>
            <div className="space-y-1.5">
              <Link href={`/app/properties/${property.id}`}><span className="flex items-center gap-2 text-xs text-blue-600 hover:underline cursor-pointer" data-testid="link-property-details"><Building2 className="h-3.5 w-3.5" /> Property Details</span></Link>
              <Link href={`/app/image-generator?propertyId=${property.id}`}><span className="flex items-center gap-2 text-xs text-blue-600 hover:underline cursor-pointer" data-testid="link-image-generator"><Camera className="h-3.5 w-3.5" /> Image Generator</span></Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function PropertyLaunchList() {
  usePageTitle("Property Launch");
  const [, navigate] = useLocation();
  const propertiesQuery = useQuery<Property[]>({ queryKey: ["/api/app/properties"] });
  const properties = propertiesQuery.data || [];
  const launchProperties = properties.filter(p => p.status === "launch");

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900" data-testid="text-launch-heading">Property Launch</h1>
          <p className="text-sm text-gray-500 mt-1">Properties in the launch phase — ready to go live on PadSplit</p>
        </div>
        <ExplainerVideoButton pageKey="property-launch" />
      </div>
      {propertiesQuery.isLoading ? (
        <div className="space-y-3"><Skeleton className="h-16 w-full" /><Skeleton className="h-16 w-full" /></div>
      ) : launchProperties.length === 0 ? (
        <div className="text-center py-16 border rounded-lg">
          <Rocket className="h-10 w-10 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500 font-medium" data-testid="text-no-launch-properties">No properties in launch phase</p>
          <p className="text-sm text-gray-400 mt-1">Change a property's status to "Launch" from the <Link href="/app/properties"><span className="text-blue-600 hover:underline cursor-pointer">Properties page</span></Link> to begin.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {launchProperties.map((property) => {
            const wd = (property.launchWizardData as WizardData) || {};
            const launched = !!property.launchedAt;
            return (
              <Link key={property.id} href={`/app/property-launch/${property.id}`} className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50 cursor-pointer transition-colors" data-testid={`launch-property-card-${property.id}`}>
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <div className="w-10 h-10 rounded-lg bg-indigo-50 flex items-center justify-center shrink-0"><Building2 className="h-5 w-5 text-indigo-600" /></div>
                  <div className="min-w-0 flex-1"><p className="font-medium text-gray-900 truncate">{property.name}</p><p className="text-sm text-gray-500 truncate">{property.address || "No address"}</p></div>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  {launched ? <Badge className="bg-green-100 text-green-700">Launched</Badge> : <Badge variant="outline" className="text-indigo-600 border-indigo-200">Part {wd.currentPart || 1}</Badge>}
                  <ChevronRight className="h-4 w-4 text-gray-400" />
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function AppPropertyLaunch() {
  const [matchDetail, params] = useRoute("/app/property-launch/:id");
  const propertyId = matchDetail ? Number(params?.id) : null;
  return (
    <AppLayout>
      <div className="max-w-5xl mx-auto px-4 py-6">
        <ViewOnlyBanner area="properties" />
        {propertyId ? <PropertyLaunchDetail propertyId={propertyId} /> : <PropertyLaunchList />}
      </div>
    </AppLayout>
  );
}
